import { useCallback, useEffect, useState } from 'react';

import { monitorApi } from '../api/monitor';
import type { ApisState, NewApiEndpoint, OperatorOption } from '../api/monitor';

/** De quanto em quanto tempo a tela relê o que o cron gravou no banco. */
const REFRESH_MS = 60_000;

export interface ApiEndpointsController {
  state: ApisState | null;
  operators: OperatorOption[];
  loading: boolean;
  /** Erro do carregamento da lista (a tela inteira falhou). */
  error: string | null;
  checking: boolean;
  saving: boolean;
  removingId: number | null;
  reload: () => Promise<void>;
  checkNow: () => Promise<void>;
  create: (payload: NewApiEndpoint) => Promise<void>;
  remove: (id: number) => Promise<void>;
}

export function useApiEndpoints(): ApiEndpointsController {
  const [state, setState] = useState<ApisState | null>(null);
  const [operators, setOperators] = useState<OperatorOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);

  const reload = useCallback(async () => {
    try {
      setState(await monitorApi.list());
      setError(null);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();

    monitorApi
      .operators()
      .then((response) => setOperators(response.operators))
      .catch(() => setOperators([]));
  }, [reload]);

  // Recarrega em segundo plano; com a aba escondida não adianta gastar request.
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void reload();
    }, REFRESH_MS);

    return () => window.clearInterval(timer);
  }, [reload]);

  const checkNow = useCallback(async () => {
    setChecking(true);
    try {
      setState(await monitorApi.checkNow());
      setError(null);
    } finally {
      setChecking(false);
    }
  }, []);

  const create = useCallback(async (payload: NewApiEndpoint) => {
    setSaving(true);
    try {
      const response = await monitorApi.create(payload);
      setState({
        apis: response.apis,
        summary: response.summary,
        intervalMinutes: response.intervalMinutes,
      });
    } finally {
      setSaving(false);
    }
  }, []);

  const remove = useCallback(async (id: number) => {
    setRemovingId(id);
    try {
      setState(await monitorApi.remove(id));
    } finally {
      setRemovingId(null);
    }
  }, []);

  return {
    state,
    operators,
    loading,
    error,
    checking,
    saving,
    removingId,
    reload,
    checkNow,
    create,
    remove,
  };
}
